import Notification from '../../shared/components/Notification/index';
import { NotificationTypes } from '../../enums/notificationTypes';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import axiosInstance from '../../interceptor/axiosInstance';
import { AxiosError } from 'axios';
import { WorkOrder } from '../../models/workorder.model';
import { InfiniteData, UseInfiniteQueryOptions, UseMutationOptions, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import { MutationKeys } from '../../enums/mutationKeys';
import { QueryKeys } from '../../enums/queryKeys';
import { infrastructureServiceMessages } from './infrastructure.constants';
import { generatePath } from 'react-router-dom';
import type { ContactFields, ContactGroup, ContactGroupOption, ContactGroupsPage } from '../../types/InfrastructureManagerTypes';

interface CustomAxiosError {
  message?: string;
}

const CONTACT_GROUPS_PAGE_SIZE = 5;

const InfrastructureManagerService = () => {
  const saveWorkOrders = (): UseMutationOptions<boolean, Error, any> => ({
    mutationKey: [MutationKeys.SAVE_WORK_ORDERS],
    mutationFn: async (workOrderData) => {
      await axiosInstance.post(ApiRoutes.WORK_ORDER, workOrderData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return true;
    },
    onSuccess: () => {
      Notification({
        message: infrastructureServiceMessages.WORK_ORDERS.SAVE_SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      const axiosError = error as AxiosError<CustomAxiosError>;
      Notification({
        message: infrastructureServiceMessages.WORK_ORDERS.SAVE_ERROR,
        description: axiosError.response?.data?.message,
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getAllWorkorders = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_ALL_WORKORDERS],
    queryFn: async () => {
      try {
        const { data } = await axiosInstance.get(ApiRoutes.WORK_ORDER);
        return data;
      } catch (ex) {
        const error = ex as Error;
        Notification({
          message: infrastructureServiceMessages.WORK_ORDERS.GET_ERROR,
          description: error.message,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const updateWorkOrderStatus = (): UseMutationOptions<WorkOrder, Error, { workOrderId: string; updatedStatus: any }> => ({
    mutationKey: [MutationKeys.UPDATE_WORK_ORDER_STATUS],
    mutationFn: async ({ workOrderId, updatedStatus }) => {
      const { status, date } = updatedStatus;
      const endpoint = generatePath(ApiRoutes.WORK_ORDER_BY_ID, {
        workOrderId,
      });
      const response = await axiosInstance.patch(endpoint, {
        status: status?.toLowerCase(),
        date: date,
      });
      return response.data;
    },
    onError: (error) => {
      const axiosError = error as AxiosError<CustomAxiosError>;
      Notification({
        message: infrastructureServiceMessages.WORK_ORDERS.UPDATE_STATUS_ERROR,
        description: axiosError.response?.data?.message,
        type: NotificationTypes.ERROR,
      });
    },
  });

  const updateWorkOrder = (): UseMutationOptions<WorkOrder, Error, { workOrderId: string; updatedWorkOrder: any }> => ({
    mutationKey: [MutationKeys.UPDATE_WORK_ORDER],
    mutationFn: async ({ workOrderId, updatedWorkOrder }) => {
      const endpoint = generatePath(ApiRoutes.WORK_ORDER_BY_ID, {
        workOrderId,
      });
      const response = await axiosInstance.put(endpoint, {
        workorder: {
          ...updatedWorkOrder,
        },
      });
      return response.data;
    },
    onError: (error) => {
      const axiosError = error as AxiosError;
      Notification({
        message: infrastructureServiceMessages.WORK_ORDERS.UPDATE_ERROR,
        description: axiosError.message,
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getAllContacts = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_ALL_CONTACTS],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.CONTACTS);
        return response.data;
      } catch (ex) {
        const error = ex as Error;
        Notification({
          message: infrastructureServiceMessages.CONTACTS.GET_ERROR,
          description: error.message,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  // Side-effect free like the Client/User Manager services; useContacts owns the toasts.
  const getContactGroups = (
    search: string,
  ): UseInfiniteQueryOptions<ContactGroupsPage, Error, InfiniteData<ContactGroupsPage, number>, [string, string], number> => ({
    queryKey: [QueryKeys.GET_CONTACT_GROUPS, search],
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await axiosInstance.get<{ data: ContactGroup[] }>(ApiRoutes.CONTACTS_GROUPS, {
        params: { page: pageParam, limit: CONTACT_GROUPS_PAGE_SIZE, search: search || undefined },
        signal,
      });
      // Pagination fields beyond page/limit are unconfirmed, so a full page is taken to mean more may follow.
      return { groups: data.data, nextPage: data.data.length === CONTACT_GROUPS_PAGE_SIZE ? pageParam + 1 : null };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    // Keeps the current list on screen while a new search loads.
    placeholderData: keepPreviousData,
  });

  // Every group (id/name only) for the move/duplicate pickers, which need groups beyond the loaded pages.
  const getContactGroupOptions = (): UseQueryOptions<ContactGroupOption[], Error> => ({
    queryKey: [QueryKeys.GET_CONTACT_GROUP_OPTIONS],
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<{ data: ContactGroupOption[] }>(ApiRoutes.CONTACTS_GROUPS, { params: { isMeta: true }, signal });
      return data.data;
    },
  });

  const createContactGroup = (): UseMutationOptions<void, Error, { name: string }> => ({
    mutationKey: [MutationKeys.CREATE_GROUP],
    mutationFn: async ({ name }) => {
      await axiosInstance.post(ApiRoutes.CONTACTS_GROUPS, { name });
    },
  });

  const renameContactGroup = (): UseMutationOptions<void, Error, { groupId: number; name: string }> => ({
    mutationKey: [MutationKeys.UPDATE_GROUP_NAME],
    mutationFn: async ({ groupId, name }) => {
      await axiosInstance.put(generatePath(ApiRoutes.CONTACTS_GROUP_BY_ID, { groupId: String(groupId) }), { name });
    },
  });

  const deleteContactGroup = (): UseMutationOptions<void, Error, { groupId: number }> => ({
    mutationKey: [MutationKeys.DELETE_GROUP],
    mutationFn: async ({ groupId }) => {
      await axiosInstance.delete(generatePath(ApiRoutes.CONTACTS_GROUP_BY_ID, { groupId: String(groupId) }));
    },
  });

  const createContact = (): UseMutationOptions<void, Error, { groupId: number; fields: ContactFields }> => ({
    mutationKey: [MutationKeys.CREATE_CONTACT],
    mutationFn: async ({ groupId, fields }) => {
      await axiosInstance.post(ApiRoutes.CONTACTS, { groupId, ...fields });
    },
  });

  const updateContact = (): UseMutationOptions<void, Error, { contactId: number; fields: Partial<ContactFields> }> => ({
    mutationKey: [MutationKeys.UPDATE_CONTACT],
    mutationFn: async ({ contactId, fields }) => {
      await axiosInstance.put(generatePath(ApiRoutes.CONTACTS_BY_ID, { id: String(contactId) }), fields);
    },
  });

  const duplicateContact = (): UseMutationOptions<void, Error, { contactId: number; groupIds: number[] }> => ({
    mutationKey: [MutationKeys.DUPLICATE_CONTACT],
    mutationFn: async ({ contactId, groupIds }) => {
      await axiosInstance.post(generatePath(ApiRoutes.CONTACT_DUPLICATE, { id: String(contactId) }), { groupIds });
    },
  });

  const moveContact = (): UseMutationOptions<void, Error, { contactId: number; fromGroupId: number; toGroupId: number }> => ({
    mutationKey: [MutationKeys.MOVE_CONTACT],
    mutationFn: async ({ contactId, fromGroupId, toGroupId }) => {
      await axiosInstance.post(generatePath(ApiRoutes.CONTACT_MOVE, { id: String(contactId) }), { fromGroupId, toGroupId });
    },
  });

  // deleteAll: true removes every copy; false removes only this group's copy.
  const deleteContact = (): UseMutationOptions<void, Error, { contactId: number; groupId: number; deleteAll: boolean }> => ({
    mutationKey: [MutationKeys.DELETE_CONTACT],
    mutationFn: async ({ contactId, groupId, deleteAll }) => {
      await axiosInstance.delete(generatePath(ApiRoutes.CONTACTS_BY_ID, { id: String(contactId) }), { params: { groupId, deleteAll } });
    },
  });

  const fetchLayersFromGeoServer = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.FETCH_LAYERS_FROM_GEOSERVER],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.GEOSERVER_FEATURE_STATS);
        return response.data;
      } catch (error) {
        const axiosError = error as AxiosError<CustomAxiosError>;
        Notification({
          message: infrastructureServiceMessages.GEOSERVER.STATS_ERROR,
          description: axiosError.response?.data?.message,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const fetchAssetListFromGeoServer = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.FETCH_ASSET_LIST_FROM_GEOSERVER],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.GEOSERVER_ASSET_LIST);
        return response.data;
      } catch (error) {
        const axiosError = error as AxiosError<CustomAxiosError>;
        Notification({
          message: infrastructureServiceMessages.GEOSERVER.STATS_ERROR,
          description: axiosError.response?.data?.message,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const getAllAssetTypeAndAssetId = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_ALL_ASSET_TYPE_AND_ASSET_ID],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.ASSET);
        return response.data;
      } catch (ex) {
        const error = ex as Error;
        Notification({
          message: infrastructureServiceMessages.ASSETS.GET_ERROR,
          description: error.message,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  return {
    saveWorkOrders,
    getAllWorkorders,
    updateWorkOrderStatus,
    updateWorkOrder,
    getAllContacts,
    getContactGroups,
    getContactGroupOptions,
    createContactGroup,
    renameContactGroup,
    deleteContactGroup,
    createContact,
    updateContact,
    duplicateContact,
    moveContact,
    deleteContact,
    fetchLayersFromGeoServer,
    fetchAssetListFromGeoServer,
    getAllAssetTypeAndAssetId,
  };
};

export default InfrastructureManagerService;
